import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CloseOutlined, Tooltip, Input, Divider, Modal } from "src/assets/imports/antdComponents";
import { IAudioVideoNarration, IAudioVideoTranscriptViewProps, MediaType, TIMING } from "../../models/interfaces";
import { isVideoMedia, formatTimestamp, highlightSearchText, getDialogHighlightRanges, highlightTextRanges, findActiveOrNearestDialogIds, sameIdList } from "../audioVideoUtils";
import { Spin, Skeleton } from "src/assets/imports/antdComponents";
import VideoPlayer from "../VideoPlayer";
import AudioPlayer from "../AudioPlayer";
import "./style.css";
import { useFeatures } from "src/config/featureManager";
import { FeatureKeysType } from "src/pages/Studies/models/interfaces";
import NarrationBlock from "./NarrationBlock";

// Where the shared <VideoPlayer> subtree currently lives. Used by the DOM-move effect to
// pick the target host, and by the transcript gates so only one TranscriptSection is
// mounted at a time.
enum PlayerLocation {
	Drawer = "drawer",
	Modal = "modal",
}

// Debounce delay for search-driven recomputes (matching, highlight, scroll). The Input's
// `value` stays bound to the immediate state so typing is responsive; only the expensive
// downstream work is deferred. Mirrors RightTranscript's SEARCH_DEBOUNCE_MS.
const SEARCH_DEBOUNCE_MS = 300;

function useDebouncedValue<T>(value: T, delayMs: number): T {
	const [debounced, setDebounced] = useState(value);
	useEffect(() => {
		const id = setTimeout(() => setDebounced(value), delayMs);
		return () => clearTimeout(id);
	}, [value, delayMs]);
	return debounced;
}

// Memoised row so re-renders triggered by playback ticks (active-dialog flip) or search-text changes
// only re-run highlight/range work for rows whose inputs actually changed (review B2).
interface IDialogRowProps {
	ti: any;
	isActive: boolean;
	isPlayingAndActive: boolean;
	isAfterNarration: boolean;
	searchQuery: string;
	highlightedDialogs: any[];
	onSeek: (seconds: number, dialogId: number) => void;
	t: (k: string) => string;
}
const DialogRow: React.FC<IDialogRowProps> = React.memo(({ ti, isActive, isPlayingAndActive, isAfterNarration, searchQuery, highlightedDialogs, onSeek, t }) => {
	const highlightRanges = useMemo(() => getDialogHighlightRanges(ti.dialog_id, highlightedDialogs), [ti.dialog_id, highlightedDialogs]);
	const isHighlighted = !!(highlightRanges && highlightRanges.length > 0);
	const dialogContent = useMemo(
		() => (isHighlighted ? highlightTextRanges(ti.dialog, highlightRanges!) : highlightSearchText(ti.dialog, searchQuery)),
		[ti.dialog, isHighlighted, highlightRanges, searchQuery],
	);
	const isPlayable = ti.timestamp_start !== null;
	const [copied, setCopied] = useState(false);
	const handleCopy = useCallback(() => {
		const speaker = ti.speaker_type ? `${ti.speaker_type}: ` : "";
		navigator.clipboard
			.writeText(`${speaker}${ti.dialog ?? ""}`)
			.then(() => {
				setCopied(true);
				setTimeout(() => setCopied(false), 2000);
			})
			.catch((err) => console.error("Failed to copy text: ", err));
	}, [ti.dialog, ti.speaker_type]);
	return (
		<div
			id={`av-dialog-${ti.dialog_id}`}
			data-dialog-id={ti.dialog_id}
			className={`av-view-dialog-item ${isActive ? "av-view-dialog-active" : ""} ${isHighlighted ? "av-view-dialog-cited" : ""} ${isAfterNarration ? "av-view-dialog-after-narration" : ""}`}>
			<div className="av-view-dialog-inner">
				<div className="av-view-dialog-header">
					<div className="av-view-dialog-speaker">
						{isPlayable ? (
							<i
								className="material-symbols-outlined filled-icon av-play-btn"
								onClick={() => onSeek(ti.timestamp_start!, ti.dialog_id)}
								role="button"
								tabIndex={0}
								aria-label={isPlayingAndActive ? t("Pause") : t("Play from this point")}
								onKeyDown={(e) => {
									if (e.key === "Enter" || e.key === " ") {
										e.preventDefault();
										onSeek(ti.timestamp_start!, ti.dialog_id);
									}
								}}>
								{isPlayingAndActive ? "pause" : "play_arrow"}
							</i>
						) : (
							<i className="material-symbols-outlined av-play-btn av-play-btn-disabled" aria-hidden="true">
								play_circle
							</i>
						)}
						<span className="av-view-speaker-name">{ti.speaker_type}</span>
					</div>
					<div className="av-view-dialog-header-right">
						<Tooltip placement="left" title={copied ? t("Copied to clipboard") : t("Copy")}>
							<i
								onClick={handleCopy}
								role="button"
								tabIndex={0}
								aria-label={t("Copy dialog")}
								onKeyDown={(e) => {
									if (e.key === "Enter" || e.key === " ") {
										e.preventDefault();
										handleCopy();
									}
								}}
								className="material-symbols-outlined av-view-dialog-copy-btn">
								{copied ? "check" : "copy_all"}
							</i>
						</Tooltip>
						<div className={`av-view-dialog-timestamp ${isActive ? "av-time-active" : "av-time-inactive"}`}>
							<span className="av-view-dialog-time-text">{formatTimestamp(ti.timestamp_start)}</span>
						</div>
					</div>
				</div>
				<div className={`av-view-dialog-content ${isActive ? "av-view-dialog-highlighted" : ""}`}>
					<p className={`av-view-dialog-text ${isHighlighted ? "highlight-container" : ""}`}>{dialogContent}</p>
				</div>
			</div>
		</div>
	);
});
DialogRow.displayName = "DialogRow";

// Aligns a dialog item near the top of the transcript scroll container, with a small offset
// below the top so the row isn't flush against the search box that sits directly above the
// scroll container (the search wrapper is a sibling and has no gap below it).
// Holds programmaticScrollCountRef > 0 for the duration of the scroll (via `scrollend` for smooth
// scrolls, double-rAF for instant) so user-scroll detection ignores the animation's own events.
const SCROLL_TOP_OFFSET = 16;
function scrollDialogIntoView(container: HTMLDivElement, dialogId: number, programmaticScrollCountRef: React.MutableRefObject<number>, behavior: ScrollBehavior = "smooth") {
	// Scope the lookup to the target container. During modal open/close, the drawer and modal
	// transcript sections briefly co-exist in the DOM (one mounting as the other unmounts).
	// `document.getElementById` would return whichever dialog row is first in DOM order, which
	// may belong to the wrong container — its rect then yields nonsense scroll math and we end
	// up scrolling to the top instead of aligning the active dialog under the search box.
	const el = container.querySelector<HTMLElement>(`#av-dialog-${dialogId}`);
	if (!el) return;

	const containerRect = container.getBoundingClientRect();
	const elRect = el.getBoundingClientRect();
	// Land the dialog's top edge SCROLL_TOP_OFFSET below the container's top edge.
	const delta = elRect.top - containerRect.top - SCROLL_TOP_OFFSET;
	// Skip micro-adjustments to avoid jitter on minor layout shifts.
	if (Math.abs(delta) < 8) return;

	// containerRect.height is post-zoom px; offsetHeight is pre-zoom CSS px — ratio corrects for CSS zoom
	const zoomFactor = containerRect.height / container.offsetHeight || 1;
	const targetScrollTop = Math.max(0, container.scrollTop + delta / zoomFactor);

	programmaticScrollCountRef.current += 1;
	container.scrollTo({ top: targetScrollTop, behavior });

	if (behavior === "instant") {
		// Instant scroll fires its scroll event before the first rAF; double-rAF ensures count
		// is still > 0 when that event fires, then drops to 0 cleanly after.
		requestAnimationFrame(() =>
			requestAnimationFrame(() => {
				programmaticScrollCountRef.current = Math.max(0, programmaticScrollCountRef.current - 1);
			}),
		);
	} else {
		// Smooth-scroll duration scales with distance — wait for the real `scrollend` event so a
		// long jump doesn't outlast a fixed timeout, with a generous safety fallback. Double-rAF
		// after settle absorbs trailing scroll events some browsers emit after scrollend.
		let settled = false;
		const release = () => {
			programmaticScrollCountRef.current = Math.max(0, programmaticScrollCountRef.current - 1);
		};
		const onSettled = () => {
			if (settled) return;
			settled = true;
			container.removeEventListener("scrollend", onSettled);
			clearTimeout(fallback);
			requestAnimationFrame(() => requestAnimationFrame(release));
		};
		container.addEventListener("scrollend", onSettled);
		const fallback = setTimeout(onSettled, 3000);
	}
}

// Memoised transcript body. Renders the dialog+narration list and is independent of the
// controlled search Input value, so typing-driven re-renders of TranscriptSection don't
// recreate the (potentially long) list. Mirrors RightTranscript's `memoizedTranscriptEntries`.
interface ITranscriptListProps {
	visibleList: Array<{ type: "narration"; n: IAudioVideoNarration } | { type: "dialog"; ti: any }>;
	activeDialogIds: number[];
	isPlaying: boolean;
	searchQuery: string;
	highlightedDialogs: any[];
	handleSeekToDialog: (time: number, id: number) => void;
	transcriptBodyRef: React.RefObject<HTMLDivElement>;
	t: (k: string) => string;
}
const TranscriptList: React.FC<ITranscriptListProps> = React.memo(
	({ visibleList, activeDialogIds, isPlaying, searchQuery, highlightedDialogs, handleSeekToDialog, transcriptBodyRef, t }) => {
		const rendered = useMemo(() => {
			return visibleList.map((item, idx) => {
				if (item.type === "narration") {
					return <NarrationBlock key={`narration-${item.n.narration_id}`} narration={item.n} searchQuery={searchQuery} />;
				}
				const ti = item.ti;
				const isActive = activeDialogIds.includes(ti.dialog_id);
				const isAfterNarration = idx > 0 && visibleList[idx - 1].type === "narration";
				return (
					<DialogRow
						key={`dialog-${ti.dialog_id}`}
						ti={ti}
						isActive={isActive}
						isPlayingAndActive={isActive && isPlaying}
						isAfterNarration={isAfterNarration}
						searchQuery={searchQuery}
						highlightedDialogs={highlightedDialogs}
						onSeek={handleSeekToDialog}
						t={t}
					/>
				);
			});
		}, [visibleList, activeDialogIds, isPlaying, searchQuery, highlightedDialogs, handleSeekToDialog, t]);

		return (
			<div className="av-view-transcript-body" ref={transcriptBodyRef}>
				{rendered}
				{visibleList.length === 0 && <div className="av-view-no-results text-[--neutral-grey]">{t("No matching conversations found")}</div>}
			</div>
		);
	},
);
TranscriptList.displayName = "TranscriptList";

const TranscriptSection: React.FC<{
	t: any;
	handleDownloadTranscript: () => void;
	highlightedTranscriptItems: any[];
	currentMentionIndex: number;
	handlePreviousMention: () => void;
	handleNextMention: () => void;
	visibleList: Array<{ type: "narration"; n: IAudioVideoNarration } | { type: "dialog"; ti: any }>;
	activeDialogIds: number[];
	autoScrollEnabled: boolean;
	onResumeAutoScroll: () => void;
	handleSeekToDialog: (time: number, id: number) => void;
	transcriptBodyRef: React.RefObject<HTMLDivElement>;
	// Immediate value bound to the Input — updates on every keystroke so typing stays responsive.
	searchInputValue: string;
	setSearchInputValue: (val: string) => void;
	// Debounced value used for downstream highlighting and the mention-nav visibility check.
	// Updating only after the user pauses keeps DialogRow / NarrationBlock from re-rendering on every key.
	searchQuery: string;
	highlightedDialogs: any[];
	isVideo: boolean;
	isPlaying: boolean;
	onSearchFocus: () => void;
	onSearchClear: () => void;
	isPausedForSearch: boolean;
	searchMatchCount: number;
	searchActiveMatchIndex: number;
	onPrevSearchMatch: () => void;
	onNextSearchMatch: () => void;
}> = React.memo(
	({
		t,
		handleDownloadTranscript,
		highlightedTranscriptItems,
		currentMentionIndex,
		handlePreviousMention,
		handleNextMention,
		visibleList,
		activeDialogIds,
		autoScrollEnabled,
		onResumeAutoScroll,
		handleSeekToDialog,
		transcriptBodyRef,
		searchInputValue,
		setSearchInputValue,
		searchQuery,
		highlightedDialogs,
		isVideo,
		isPlaying,
		onSearchFocus,
		onSearchClear,
		isPausedForSearch,
		searchMatchCount,
		searchActiveMatchIndex,
		onPrevSearchMatch,
		onNextSearchMatch,
	}) => {
		return (
			<>
				{/* Transcripts section header */}
				<div className="av-transcript-section-wrapper">
					<div className="av-view-section-header">
						<span className="av-view-section-title secondary-color">{t("Transcripts")}</span>
						<div className="av-view-section-header-right">
							{highlightedTranscriptItems.length > 1 && !searchQuery.trim() && (
								<div className="mention-navigation-floating av-mention-nav">
									<div className="mention-navigation-label">
										{currentMentionIndex + 1} / {highlightedTranscriptItems.length}
									</div>
									<Divider type="vertical" className="mention-divider" />
									<button
										onClick={handlePreviousMention}
										disabled={currentMentionIndex === 0}
										aria-label={t("Previous mention")}
										className={`mention-nav-button ${currentMentionIndex === 0 ? "mention-nav-disabled" : ""}`}>
										<i className="material-symbols-outlined" aria-hidden="true">
											keyboard_arrow_up
										</i>
									</button>
									<button
										onClick={handleNextMention}
										disabled={currentMentionIndex === highlightedTranscriptItems.length - 1}
										aria-label={t("Next mention")}
										className={`mention-nav-button ${currentMentionIndex === highlightedTranscriptItems.length - 1 ? "mention-nav-disabled" : ""}`}>
										<i className="material-symbols-outlined" aria-hidden="true">
											keyboard_arrow_down
										</i>
									</button>
								</div>
							)}
							<div className="av-view-section-actions">
								<Tooltip title={t("Download transcript")} placement="topRight">
									<i
										className="material-symbols-outlined cursor-pointer"
										onClick={handleDownloadTranscript}
										role="button"
										tabIndex={0}
										aria-label={t("Download transcript")}
										onKeyDown={(e) => {
											if (e.key === "Enter" || e.key === " ") {
												e.preventDefault();
												handleDownloadTranscript();
											}
										}}>
										download
									</i>
								</Tooltip>
							</div>
						</div>
					</div>
					<div className="av-view-search">
						<Input
							prefix={<i className="material-symbols-outlined text-[--neutral-grey] text-xl">search</i>}
							placeholder={t("Search conversation...")}
							value={searchInputValue}
							onChange={(e) => {
								const newValue = e.target.value;
								const wasEmpty = !searchInputValue;
								setSearchInputValue(newValue);
								if (newValue === "") {
									onSearchClear();
								} else if (wasEmpty) {
									// Catches the case where the input was already focused (so
									// onFocus won't fire again) but the user only starts typing
									// after playback began — ensures the video still pauses.
									onSearchFocus();
								}
							}}
							onFocus={onSearchFocus}
							suffix={
								searchInputValue.trim() ? (
									<div className="transcript-search-nav" onMouseDown={(e) => e.preventDefault()}>
										{searchMatchCount > 0 ? (
											<>
												<span className="transcript-search-count">
													{searchActiveMatchIndex + 1}/{searchMatchCount}
												</span>
												<button type="button" className="transcript-search-nav-btn" onClick={onPrevSearchMatch} aria-label={t("Previous match")}>
													<i className="material-symbols-outlined" aria-hidden="true">
														keyboard_arrow_up
													</i>
												</button>
												<button type="button" className="transcript-search-nav-btn" onClick={onNextSearchMatch} aria-label={t("Next match")}>
													<i className="material-symbols-outlined" aria-hidden="true">
														keyboard_arrow_down
													</i>
												</button>
											</>
										) : (
											<span className="transcript-search-no-result">{t("No Results")}</span>
										)}
										<button
											type="button"
											className="transcript-search-clear-btn"
											onClick={() => {
												setSearchInputValue("");
												onSearchClear();
											}}
											aria-label={t("Clear search")}>
											<i className="material-symbols-outlined" aria-hidden="true">
												cancel
											</i>
										</button>
									</div>
								) : null
							}
							className="av-view-search-input av-search-input-larger"
						/>
					</div>
				</div>

				{/* Transcript dialog list — children are memoised so typing-only re-renders of
					 TranscriptSection (caused by the controlled search Input) reuse cached
					 elements and let React skip the entire subtree reconciliation. */}
				<TranscriptList
					visibleList={visibleList}
					activeDialogIds={activeDialogIds}
					isPlaying={isPlaying}
					searchQuery={searchQuery}
					highlightedDialogs={highlightedDialogs}
					handleSeekToDialog={handleSeekToDialog}
					transcriptBodyRef={transcriptBodyRef}
					t={t}
				/>

				{/* Sync button — shown when auto-scroll is off or paused for search */}
				{(!autoScrollEnabled || isPausedForSearch) && (
					<div className="av-view-sync-container">
						<button className="av-view-sync-btn" onClick={onResumeAutoScroll} aria-label={isVideo ? t("Sync with video") : t("Sync with audio")}>
							<i className="material-symbols-outlined av-view-sync-icon" aria-hidden="true">
								arrow_upward
							</i>
							{isVideo ? t("Sync with video") : t("Sync with audio")}
						</button>
					</div>
				)}
			</>
		);
	},
);

const AudioVideoTranscriptView: React.FC<IAudioVideoTranscriptViewProps> = ({
	item,
	onClose,
	isExpanded,
	handleExpand,
	transcriptItems,
	mediaUrl,
	mediaType,
	isLoadingAV,
	highlightedDialogs = [],
	firstDialogNo,
	presignedSegmentUrls = [],
	onDownloadTranscript,
	narrations = [],
}) => {
	const { t } = useTranslation();
	const { isFeatureAvailable } = useFeatures();
	const isAudioVideoEnabled = isFeatureAvailable(FeatureKeysType.AudioVideo);
	const isAvNarrationsFlag = isFeatureAvailable(FeatureKeysType.AvNarrations);
	const isAvNarrationsEnabled = isAudioVideoEnabled && isAvNarrationsFlag;

	const narrationsByDialogId = useMemo(() => {
		const map = new Map<number, IAudioVideoNarration[]>();
		if (!isAvNarrationsEnabled || !Array.isArray(narrations)) return map;
		for (const n of narrations) {
			if (n?.next_dialog_id == null) continue;
			const list = map.get(n.next_dialog_id) ?? [];
			list.push(n);
			map.set(n.next_dialog_id, list);
		}
		return map;
	}, [narrations, isAvNarrationsEnabled]);

	const transcriptBodyRef = useRef<HTMLDivElement>(null);
	const programmaticScrollCountRef = useRef(0);
	const pendingPlayAfterSeekRef = useRef(false);
	const hasInitialScrolledRef = useRef(false);
	const hasInitialSeekedRef = useRef(false);
	const wasPlayingBeforeSearchRef = useRef(false);
	const activeMarkRef = useRef<HTMLElement | null>(null);
	const searchScrollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	// Mirrors `isPlaying` so search-focus handlers always read the latest playback state
	// even when their closure was created in an earlier render (matches RightTranscript).
	const isPlayingRef = useRef(false);

	const [currentTime, setCurrentTime] = useState(0);
	// `searchInput` drives the visible Input value (immediate). `searchQuery` is the debounced
	// version that downstream memos/effects/highlighting depend on — keeps typing responsive.
	const [searchInput, setSearchInput] = useState("");
	const searchQuery = useDebouncedValue(searchInput, SEARCH_DEBOUNCE_MS);
	const [searchActiveMatchIndex, setSearchActiveMatchIndex] = useState(-1);
	const [activeDialogIds, setActiveDialogIds] = useState<number[]>([]);
	const [seekToTime, setSeekToTime] = useState<number | undefined>(undefined);
	const [seekTrigger, setSeekTrigger] = useState(0);
	const [isPlaying, setIsPlaying] = useState(false);
	const [pauseTrigger, setPauseTrigger] = useState(0);
	const [playTrigger, setPlayTrigger] = useState(0);
	const [playerReady, setPlayerReady] = useState(false);
	const [isPausedForSearch, setIsPausedForSearch] = useState(false);
	const [autoScrollEnabled, setAutoScrollEnabled] = useState(true);
	const [currentMentionIndex, setCurrentMentionIndex] = useState(0);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const isModalOpenRef = useRef(false);
	const modalTranscriptBodyRef = useRef<HTMLDivElement>(null);

	// DOM-move pattern: <VideoPlayer> is rendered exactly once inside playerMountRef and physically
	// moved between drawer and modal hosts via appendChild on expand toggle, so the underlying
	// <video>, hls.js binding, MediaSource buffer, and play state survive (re-mounting would lose
	// all of them). playerLocation is updated via Modal.afterOpenChange on close so the move-back
	// waits for the Antd close animation, avoiding a flash of empty modal.
	const drawerPlayerHostRef = useRef<HTMLDivElement>(null);
	const modalPlayerHostRef = useRef<HTMLDivElement>(null);
	const playerMountRef = useRef<HTMLDivElement>(null);
	const isMountedRef = useRef(true);
	const [playerLocation, setPlayerLocation] = useState<PlayerLocation>(PlayerLocation.Drawer);

	const isVideo = useMemo(() => {
		if (mediaType === MediaType.Video) return true;
		if (mediaType === MediaType.Audio) return false;
		return isVideoMedia(item);
	}, [mediaType, item]);

	const dialogNos = useMemo(() => highlightedDialogs.map((d) => parseInt(d.dialog_no, 10)).filter((n) => !isNaN(n)), [highlightedDialogs]);

	const highlightedTranscriptItems = useMemo(
		() => (Array.isArray(transcriptItems) ? transcriptItems.filter((ti) => dialogNos.includes(ti.dialog_id)) : []),
		[transcriptItems, dialogNos],
	);

	// Flat ordered list of narrations + dialogs. All entries stay visible while a search is
	// active; matches are highlighted in place and stepped through via searchActiveMatchIndex.
	const visibleList = useMemo(() => {
		const items: Array<{ type: "narration"; n: IAudioVideoNarration } | { type: "dialog"; ti: any }> = [];
		if (!Array.isArray(transcriptItems)) return items;
		for (const ti of transcriptItems) {
			const narrations = narrationsByDialogId.get(ti.dialog_id);
			if (narrations) for (const n of narrations) items.push({ type: "narration", n });
			items.push({ type: "dialog", ti });
		}
		return items;
	}, [transcriptItems, narrationsByDialogId]);

	// Per-occurrence match list across narrations and dialogs in source order.
	// occurrenceIndex is the 0-based index of the match within its parent dialog/narration so
	// the scroll-to-match effect can target the exact <mark> after rendering.
	const searchMatches = useMemo<Array<{ type: "dialog" | "narration"; id: number; occurrenceIndex: number }>>(() => {
		const q = searchQuery.trim().toLowerCase();
		if (!q || !Array.isArray(transcriptItems)) return [];
		const matches: Array<{ type: "dialog" | "narration"; id: number; occurrenceIndex: number }> = [];
		for (const ti of transcriptItems) {
			const narrations = narrationsByDialogId.get(ti.dialog_id);
			if (narrations) {
				for (const n of narrations) {
					const lower = (n.content ?? "").toLowerCase();
					let occ = 0;
					let idx = 0;
					while ((idx = lower.indexOf(q, idx)) !== -1) {
						matches.push({ type: "narration", id: n.narration_id, occurrenceIndex: occ++ });
						idx += q.length;
					}
				}
			}
			const lower = (ti.dialog ?? "").toLowerCase();
			let occ = 0;
			let idx = 0;
			while ((idx = lower.indexOf(q, idx)) !== -1) {
				matches.push({ type: "dialog", id: ti.dialog_id, occurrenceIndex: occ++ });
				idx += q.length;
			}
		}
		return matches;
	}, [searchQuery, transcriptItems, narrationsByDialogId]);

	useEffect(() => {
		setSearchActiveMatchIndex(searchMatches.length > 0 ? 0 : -1);
	}, [searchQuery, searchMatches.length]);

	const moveSearchMatch = useCallback(
		(direction: number) => {
			setSearchActiveMatchIndex((prev) => {
				if (!searchMatches.length) return -1;
				if (prev < 0) return direction > 0 ? 0 : searchMatches.length - 1;
				return (prev + direction + searchMatches.length) % searchMatches.length;
			});
		},
		[searchMatches.length],
	);

	// Stable callbacks so TranscriptSection's React.memo isn't busted on every keystroke
	// (inline arrows would otherwise create new fn references each parent render).
	const handlePrevSearchMatch = useCallback(() => moveSearchMatch(-1), [moveSearchMatch]);
	const handleNextSearchMatch = useCallback(() => moveSearchMatch(1), [moveSearchMatch]);

	useEffect(() => {
		return () => {
			if (searchScrollTimerRef.current) {
				clearTimeout(searchScrollTimerRef.current);
				searchScrollTimerRef.current = null;
				programmaticScrollCountRef.current = Math.max(0, programmaticScrollCountRef.current - 1);
			}
		};
	}, []);

	useEffect(() => {
		if (activeMarkRef.current) {
			activeMarkRef.current.classList.remove("av-search-highlight-active");
			activeMarkRef.current = null;
		}

		const match = searchMatches[searchActiveMatchIndex];
		const container = isModalOpenRef.current ? modalTranscriptBodyRef.current : transcriptBodyRef.current;
		if (!searchQuery.trim() || !match || !container) return;

		let element: HTMLElement | null = null;
		let activeMark: HTMLElement | null = null;

		if (match.type === "dialog") {
			const entry = container.querySelector<HTMLElement>(`#av-dialog-${match.id}`);
			if (entry) {
				const marks = entry.querySelectorAll<HTMLElement>("mark.av-search-highlight");
				activeMark = marks[match.occurrenceIndex] ?? null;
				element = activeMark ?? marks[0] ?? entry;
			}
		} else {
			const wrapper = container.querySelector<HTMLElement>(`[data-narration-id="${match.id}"]`);
			if (wrapper) {
				const marks = wrapper.querySelectorAll<HTMLElement>("mark.av-search-highlight");
				activeMark = marks[match.occurrenceIndex] ?? null;
				element = activeMark ?? marks[0] ?? wrapper;
			}
		}

		if (activeMark) {
			activeMark.classList.add("av-search-highlight-active");
			activeMarkRef.current = activeMark;
		}

		if (!element) return;

		if (searchScrollTimerRef.current) {
			clearTimeout(searchScrollTimerRef.current);
		} else {
			programmaticScrollCountRef.current += 1;
		}

		const containerRect = container.getBoundingClientRect();
		const elementRect = element.getBoundingClientRect();
		const scrollOffset = elementRect.top - containerRect.top - containerRect.height / 2 + elementRect.height / 2;
		container.scrollBy({ top: scrollOffset, behavior: "smooth" });

		searchScrollTimerRef.current = setTimeout(() => {
			programmaticScrollCountRef.current = Math.max(0, programmaticScrollCountRef.current - 1);
			searchScrollTimerRef.current = null;
		}, 900);
	}, [searchActiveMatchIndex, searchMatches, searchQuery, playerLocation]);

	// Update active dialogs from playback time (review A6).
	// Binary-searches the sorted transcript and skips needless setState commits when the
	// id list is unchanged — keeps the timeupdate tick (~4 Hz) from cascading rerenders.
	// When playback is in a silence gap, anchors on the most recent past dialog so the
	// transcript keeps scroll/highlight in sync instead of stranding on an empty selection.
	useEffect(() => {
		const next = findActiveOrNearestDialogIds(transcriptItems, currentTime);
		setActiveDialogIds((prev) => (sameIdList(prev, next) ? prev : next));
	}, [currentTime, transcriptItems]);

	// Imperatively re-sync activeDialogIds and scroll the just-mounted transcript body to the
	// active dialog. Called on modal open (target = modal body) and on modal close after the
	// drawer transcript has re-mounted (target = drawer body). The DOM-move of the <video>
	// can fire a transient timeupdate=0 mid-move that otherwise leaves the new transcript with
	// an empty active list and no highlight; reading the live video time bypasses that.
	const syncActiveDialogAndScroll = useCallback(
		(target: PlayerLocation) => {
			const video = playerMountRef.current?.querySelector("video") as HTMLVideoElement | null;
			const t = video?.currentTime ?? 0;
			const next = findActiveOrNearestDialogIds(transcriptItems, t);
			if (next.length === 0) return;
			setActiveDialogIds(next);
			// Defer the scroll so the gated TranscriptSection has had a commit to attach its ref.
			setTimeout(() => {
				const body = target === PlayerLocation.Modal ? modalTranscriptBodyRef.current : transcriptBodyRef.current;
				if (!body) return;
				scrollDialogIntoView(body, next[0], programmaticScrollCountRef, "smooth");
			}, TIMING?.SCROLL_DELAY || 150);
		},
		[transcriptItems],
	);

	// Auto-scroll to active dialog during playback.
	// Runs when activeDialogIds changes (timeupdate) or when autoScrollEnabled is re-enabled.
	useEffect(() => {
		if (!autoScrollEnabled || activeDialogIds.length === 0) return;
		const activeRef = isModalOpenRef.current ? modalTranscriptBodyRef.current : transcriptBodyRef.current;
		if (!activeRef) return;
		scrollDialogIntoView(activeRef, activeDialogIds[0], programmaticScrollCountRef, "smooth");
	}, [activeDialogIds, autoScrollEnabled, playerLocation]);

	// Initial scroll to the first cited dialog after content loads
	useEffect(() => {
		if (isLoadingAV || !transcriptItems?.length || !highlightedTranscriptItems?.length) return;
		if (hasInitialScrolledRef.current) return;

		const targetId = firstDialogNo ? parseInt(firstDialogNo) : highlightedTranscriptItems[0]?.dialog_id;
		if (targetId == null) return;

		const timerId = setTimeout(() => {
			const activeRef = isModalOpenRef.current ? modalTranscriptBodyRef.current : transcriptBodyRef.current;
			if (!activeRef) return;
			scrollDialogIntoView(activeRef, targetId, programmaticScrollCountRef, "smooth");
			hasInitialScrolledRef.current = true;
		}, TIMING?.SCROLL_DELAY || 150);

		return () => clearTimeout(timerId);
	}, [isLoadingAV, transcriptItems, highlightedTranscriptItems, firstDialogNo]);

	// Initial player seek to the first cited dialog's timestamp
	useEffect(() => {
		if (!playerReady || isLoadingAV || !transcriptItems?.length || !highlightedTranscriptItems?.length) return;
		if (hasInitialSeekedRef.current) return;

		const targetId = firstDialogNo ? parseInt(firstDialogNo) : highlightedTranscriptItems[0]?.dialog_id;
		if (targetId == null) return;

		const targetItem = (transcriptItems ?? []).find((ti) => ti.dialog_id === targetId);
		if (targetItem?.timestamp_start != null && targetItem.timestamp_start > 0) {
			hasInitialSeekedRef.current = true;
			setSeekToTime(targetItem.timestamp_start);
			setSeekTrigger((p) => p + 1);
			setPlayTrigger((p) => p + 1);
			setActiveDialogIds([targetItem.dialog_id]);
		}
	}, [playerReady, isLoadingAV, transcriptItems, highlightedTranscriptItems, firstDialogNo]);

	// Reset all scroll/seek tracking when highlighted dialogs change
	useEffect(() => {
		hasInitialScrolledRef.current = false;
		hasInitialSeekedRef.current = false;
		setCurrentMentionIndex(0);
		setAutoScrollEnabled(true);
		setPlayerReady(false);
	}, [highlightedDialogs]);

	// Disable auto-scroll only when the scroll is actually driven by the user. Gating on
	// `programmaticScrollCountRef` alone is fragile because trailing scroll events (from the
	// smooth-scroll animation settling, or a layout shift) would otherwise be mis-attributed to
	// the user. Requiring a recent wheel/touch/key event filters those out.
	useEffect(() => {
		const body = isModalOpenRef.current ? modalTranscriptBodyRef.current : transcriptBodyRef.current;
		if (!body || isLoadingAV) return;

		let lastUserInputAt = 0;
		const markUserInput = () => {
			lastUserInputAt = performance.now();
		};
		const handleScroll = () => {
			if (programmaticScrollCountRef.current > 0) return;
			if (performance.now() - lastUserInputAt > 500) return;
			setAutoScrollEnabled(false);
		};

		body.addEventListener("wheel", markUserInput, { passive: true });
		body.addEventListener("touchstart", markUserInput, { passive: true });
		body.addEventListener("touchmove", markUserInput, { passive: true });
		body.addEventListener("keydown", markUserInput);
		body.addEventListener("scroll", handleScroll, { passive: true });
		return () => {
			body.removeEventListener("wheel", markUserInput);
			body.removeEventListener("touchstart", markUserInput);
			body.removeEventListener("touchmove", markUserInput);
			body.removeEventListener("keydown", markUserInput);
			body.removeEventListener("scroll", handleScroll);
		};
	}, [isLoadingAV, playerLocation]);

	// Keep currentMentionIndex in sync with which highlighted dialog is most visible.
	// Uses IntersectionObserver instead of a per-scroll-tick getBoundingClientRect loop
	// to avoid forced layout on long transcripts (review A7).
	useEffect(() => {
		const body = isModalOpenRef.current ? modalTranscriptBodyRef.current : transcriptBodyRef.current;
		if (!body || highlightedTranscriptItems.length === 0) return;

		const ratios = new Map<number, number>();
		const io = new IntersectionObserver(
			(entries) => {
				if (programmaticScrollCountRef.current > 0) return;
				entries.forEach((e) => {
					const id = Number((e.target as HTMLElement).dataset.dialogId);
					if (Number.isNaN(id)) return;
					ratios.set(id, e.intersectionRatio);
				});
				let bestIdx: number | null = null;
				let bestScore = -Infinity;
				for (let i = 0; i < highlightedTranscriptItems.length; i++) {
					const r = ratios.get(highlightedTranscriptItems[i].dialog_id) ?? 0;
					// Top-of-list tiebreaker so the earliest visible mention wins on ties.
					const score = r - i * 1e-6;
					if (score > bestScore) {
						bestScore = score;
						bestIdx = i;
					}
				}
				if (bestIdx !== null && bestScore > 0) {
					setCurrentMentionIndex((prev) => (prev !== bestIdx ? (bestIdx as number) : prev));
				}
			},
			{ root: body, threshold: [0, 0.25, 0.5, 0.75, 1] },
		);

		for (const ti of highlightedTranscriptItems) {
			const el = document.getElementById(`av-dialog-${ti.dialog_id}`);
			if (el) io.observe(el);
		}
		return () => io.disconnect();
	}, [highlightedTranscriptItems, playerLocation]);

	const handlePreviousMention = useCallback(() => {
		if (currentMentionIndex <= 0) return;
		const newIndex = currentMentionIndex - 1;
		setAutoScrollEnabled(false);
		setCurrentMentionIndex(newIndex);
		const targetItem = highlightedTranscriptItems[newIndex];
		const activeRef = isModalOpenRef.current ? modalTranscriptBodyRef.current : transcriptBodyRef.current;
		if (targetItem && activeRef) {
			scrollDialogIntoView(activeRef, targetItem.dialog_id, programmaticScrollCountRef, "smooth");
		}
	}, [currentMentionIndex, highlightedTranscriptItems]);

	const handleNextMention = useCallback(() => {
		if (currentMentionIndex >= highlightedTranscriptItems.length - 1) return;
		const newIndex = currentMentionIndex + 1;
		setAutoScrollEnabled(false);
		setCurrentMentionIndex(newIndex);
		const targetItem = highlightedTranscriptItems[newIndex];
		const activeRef = isModalOpenRef.current ? modalTranscriptBodyRef.current : transcriptBodyRef.current;
		if (targetItem && activeRef) {
			scrollDialogIntoView(activeRef, targetItem.dialog_id, programmaticScrollCountRef, "smooth");
		}
	}, [currentMentionIndex, highlightedTranscriptItems]);

	const handleTimeUpdate = useCallback((time: number) => {
		setCurrentTime(time);
	}, []);

	const handlePlayerSeeked = useCallback(
		(time: number) => {
			if (pendingPlayAfterSeekRef.current) {
				// Seek was triggered by handleSeekToDialog — just start playback
				pendingPlayAfterSeekRef.current = false;
				setPlayTrigger((p) => p + 1);
				return;
			}
			// User-initiated scrubber seek — update active dialog from seek time and re-sync scroll.
			// This is necessary because timeupdate won't fire if media is paused, so activeDialogIds
			// would never change and the transcript would not scroll to the seeked position.
			// Falls back to the nearest past dialog when the seek lands in a silence gap so the
			// transcript still scrolls/highlights instead of being left empty. Always assigns a
			// fresh array so the auto-scroll effect re-fires even when seeking to the same dialog.
			const next = findActiveOrNearestDialogIds(transcriptItems, time);
			if (next.length > 0) setActiveDialogIds(next);
			setAutoScrollEnabled(true);
		},
		[transcriptItems],
	);

	const handleSeekToDialog = useCallback(
		(seconds: number, dialogId: number) => {
			if (activeDialogIds.indexOf(dialogId) !== -1) {
				isPlaying ? setPauseTrigger((p) => p + 1) : setPlayTrigger((p) => p + 1);
				return;
			}
			// Seek to inactive dialog, then play via onSeeked callback
			pendingPlayAfterSeekRef.current = true;
			setSeekToTime(seconds);
			setSeekTrigger((p) => p + 1);
			setActiveDialogIds([dialogId]);
			setAutoScrollEnabled(true);

			// Safety fallback if onSeeked doesn't fire
			setTimeout(() => {
				if (pendingPlayAfterSeekRef.current) {
					pendingPlayAfterSeekRef.current = false;
					setPlayTrigger((p) => p + 1);
				}
			}, 1000);
		},
		[activeDialogIds, isPlaying],
	);

	const handlePlayStateChange = useCallback((playing: boolean) => {
		isPlayingRef.current = playing;
		setIsPlaying(playing);
	}, []);

	const handlePlayerReady = useCallback(() => {
		setPlayerReady(true);
	}, []);

	const handleDownloadTranscript = useCallback(() => {
		onDownloadTranscript?.();
	}, [onDownloadTranscript]);

	const handleResumeAutoScroll = useCallback(() => {
		setSearchInput("");
		setAutoScrollEnabled(true);
		setIsPausedForSearch(false);
		if (wasPlayingBeforeSearchRef.current) {
			setPlayTrigger((p) => p + 1);
			wasPlayingBeforeSearchRef.current = false;
		}
		requestAnimationFrame(() => {
			const activeRef = isModalOpenRef.current ? modalTranscriptBodyRef.current : transcriptBodyRef.current;
			if (activeRef && activeDialogIds.length > 0) {
				scrollDialogIntoView(activeRef, activeDialogIds[0], programmaticScrollCountRef, "smooth");
			}
		});
	}, [activeDialogIds]);

	const handleSearchFocus = useCallback(() => {
		// Re-entry guard: a clear-then-retype cycle keeps the input focused, so onChange
		// also calls this. Without the guard the second call would overwrite the
		// "was playing" ref with the just-paused (false) state.
		if (isPausedForSearch) return;
		wasPlayingBeforeSearchRef.current = isPlayingRef.current;
		if (isPlayingRef.current) {
			setPauseTrigger((p) => p + 1);
			setIsPausedForSearch(true);
		}
	}, [isPausedForSearch]);

	const handleSearchClear = useCallback(() => {
		if (wasPlayingBeforeSearchRef.current) {
			setPlayTrigger((p) => p + 1);
			wasPlayingBeforeSearchRef.current = false;
		}
		setIsPausedForSearch(false);
	}, []);

	const handleOpenModal = useCallback(() => {
		setIsModalOpen(true);
		setPlayerLocation(PlayerLocation.Modal);
		isModalOpenRef.current = true;
		// Scroll is run from afterOpenChange so it fires after the modal open animation
		// settles — running it now would compute the target scrollTop against a mid-animation
		// layout and leave the active dialog misaligned with the search box.
	}, []);

	const handleCloseModal = useCallback(() => {
		setIsModalOpen(false);
		// playerLocation and isModalOpenRef stay at PlayerLocation.Modal/true until afterOpenChange fires after the
		// close animation — keeps the modal-side transcript body live for the duration so playback,
		// auto-scroll, and intersection tracking continue to operate on the visible transcript.
	}, []);

	// Move the <VideoPlayer> DOM subtree between drawer and modal hosts.
	// VideoPlayer's <video> + hls.js binding stay attached to the same node so playback survives.
	// Idempotent on re-fire (StrictMode / dep changes) via the parent-element check.
	useEffect(() => {
		if (!isVideo) return;
		const mount = playerMountRef.current;
		if (!mount) return;
		const drawerHost = drawerPlayerHostRef.current;
		const modalHost = modalPlayerHostRef.current;
		const target = playerLocation === PlayerLocation.Modal ? modalHost : drawerHost;
		if (!target) return;
		if (mount.parentElement === target) return;

		const video = mount.querySelector("video") as HTMLVideoElement | null;
		const wasPlaying = video ? !video.paused : false;
		const savedTime = video?.currentTime ?? 0;
		const savedVolume = video?.volume ?? 1;
		const savedPlaybackRate = video?.playbackRate ?? 1;

		try {
			target.appendChild(mount);
			requestAnimationFrame(() => {
				if (!isMountedRef.current || !video) return;
				// Safari has historically paused media on reparent — restore state defensively.
				video.currentTime = savedTime;
				video.volume = savedVolume;
				video.playbackRate = savedPlaybackRate;
				if (wasPlaying) {
					video.play().catch((err: DOMException) => {
						if (err.name !== "AbortError") console.error("[AV] resume after move failed", err);
					});
				}
			});
		} catch (err) {
			console.error("[AV] DOM move failed", err);
		}
	}, [isVideo, playerLocation, isLoadingAV, mediaUrl]);

	// On unmount, ensure the player sits inside its React-owned drawer host so React's reconciler
	// can detach it cleanly. Without this, if the modal is open during unmount React would call
	// drawerHost.removeChild(node) where node.parentNode is the (separately-portalled) modal host.
	useEffect(() => {
		return () => {
			isMountedRef.current = false;
			const mount = playerMountRef.current;
			const drawerHost = drawerPlayerHostRef.current;
			if (mount && drawerHost && mount.parentElement !== drawerHost) {
				try {
					drawerHost.appendChild(mount);
				} catch {
					// drawer host may already be detached — nothing to do
				}
			}
		};
	}, []);

	return (
		<div className="av-transcript-view">
			{/* Header */}
			<div className="av-view-header">
				<div className="av-view-header-left">
					<span className="av-view-file-name secondary-color">{item?.file_name}</span>
					{item?.created_at && (
						<span className="av-view-file-date text-[--neutral-grey]">
							[{new Date(item.created_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "2-digit" })},{" "}
							{new Date(item.created_at).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit", hour12: true })}]
						</span>
					)}
				</div>
				<div className="av-view-header-right">
					<i
						className="material-symbols-outlined text-[1.5rem] cursor-pointer text-[--secondary-color]"
						onClick={isVideo ? handleOpenModal : handleExpand}
						role="button"
						tabIndex={0}
						aria-label={isVideo ? t("Expand to full view") : isExpanded ? t("Collapse") : t("Expand")}
						onKeyDown={(e) => {
							if (e.key === "Enter" || e.key === " ") {
								e.preventDefault();
								(isVideo ? handleOpenModal : handleExpand)();
							}
						}}>
						{isVideo ? "expand_content" : isExpanded ? "hide" : "expand_content"}
					</i>
					<CloseOutlined onClick={onClose} className="secondary-color text-base cursor-pointer" />
				</div>
			</div>

			{isLoadingAV ? (
				<div className="flex items-center justify-center flex-1">
					<Spin size="large" />
				</div>
			) : (
				<>
					<div className="av-view-player-wrapper">
						<div ref={drawerPlayerHostRef} className="av-view-player-host">
							{isVideo ? (
								// playerMountRef wraps a single <VideoPlayer>; the move effect appendChild's
								// this div between drawerPlayerHostRef and modalPlayerHostRef without remounting.
								<div ref={playerMountRef} className="av-view-player-mount">
									<VideoPlayer
										mediaUrl={mediaUrl}
										duration={item?.media_duration}
										durationSeconds={item?.media_duration_seconds}
										ariaLabel={item?.file_name ? `Video player — ${item.file_name}` : "Video player"}
										onTimeUpdate={handleTimeUpdate}
										onSeeked={handlePlayerSeeked}
										onPlayStateChange={handlePlayStateChange}
										onReady={handlePlayerReady}
										seekToTime={seekToTime}
										seekTrigger={seekTrigger}
										pauseTrigger={pauseTrigger}
										playTrigger={playTrigger}
										presignedSegmentUrls={presignedSegmentUrls}
									/>
								</div>
							) : (
								<AudioPlayer
									mediaUrl={mediaUrl}
									duration={item?.media_duration}
									durationSeconds={item?.media_duration_seconds}
									onTimeUpdate={handleTimeUpdate}
									onSeeked={handlePlayerSeeked}
									onPlayStateChange={handlePlayStateChange}
									onReady={handlePlayerReady}
									seekToTime={seekToTime}
									seekTrigger={seekTrigger}
									pauseTrigger={pauseTrigger}
									playTrigger={playTrigger}
									presignedSegmentUrls={presignedSegmentUrls}
								/>
							)}
						</div>
					</div>

					{/* Gate on playerLocation rather than isModalOpen so the drawer transcript only
					    appears after the modal close animation finishes — matches the player's move-back
					    timing and avoids a transient state where both transcripts are mounted. */}
					{playerLocation === PlayerLocation.Drawer && (
						<TranscriptSection
							t={t}
							handleDownloadTranscript={handleDownloadTranscript}
							highlightedTranscriptItems={highlightedTranscriptItems}
							currentMentionIndex={currentMentionIndex}
							handlePreviousMention={handlePreviousMention}
							handleNextMention={handleNextMention}
							visibleList={visibleList}
							activeDialogIds={activeDialogIds}
							handleSeekToDialog={handleSeekToDialog}
							transcriptBodyRef={transcriptBodyRef}
							searchInputValue={searchInput}
							setSearchInputValue={setSearchInput}
							searchQuery={searchQuery}
							highlightedDialogs={highlightedDialogs}
							autoScrollEnabled={autoScrollEnabled}
							onResumeAutoScroll={handleResumeAutoScroll}
							isVideo={isVideo}
							isPlaying={isPlaying}
							onSearchFocus={handleSearchFocus}
							onSearchClear={handleSearchClear}
							isPausedForSearch={isPausedForSearch}
							searchMatchCount={searchMatches.length}
							searchActiveMatchIndex={searchActiveMatchIndex}
							onPrevSearchMatch={handlePrevSearchMatch}
							onNextSearchMatch={handleNextSearchMatch}
						/>
					)}
				</>
			)}
			{isVideo && (
				<Modal
					open={isModalOpen}
					onCancel={handleCloseModal}
					footer={null}
					width="75%"
					centered
					forceRender
					closable={false}
					afterOpenChange={(open) => {
						setPlayerLocation(open ? PlayerLocation.Modal : PlayerLocation.Drawer);
						isModalOpenRef.current = open;
						syncActiveDialogAndScroll(open ? PlayerLocation.Modal : PlayerLocation.Drawer);
					}}
					className="av-expanded-modal"
					rootClassName="av-expanded-modal-root"
					getContainer={() => (document.querySelector(".scale-baseline-100") as HTMLElement) ?? document.body}
					styles={{ body: { padding: 0 } }}>
					<div className="av-modal-root">
						<div className="av-modal-header">
							<div className="av-view-header-left">
								<span className="av-view-file-name secondary-color">{item?.file_name}</span>
								{item?.created_at && (
									<span className="av-view-file-date text-[--neutral-grey]">
										[{new Date(item.created_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "2-digit" })},{" "}
										{new Date(item.created_at).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit", hour12: true })}]
									</span>
								)}
							</div>
							<div className="av-view-header-right">
								<CloseOutlined onClick={handleCloseModal} className="secondary-color text-base cursor-pointer" />
							</div>
						</div>
						<div className="av-modal-body">
							<div className="av-modal-player-col">
								{/* Empty host — the move effect appendChild's playerMountRef into this div. */}
								<div ref={modalPlayerHostRef} className="av-modal-player-host">
									{isLoadingAV && <Skeleton.Image active className="av-modal-player-skeleton" />}
								</div>
							</div>
							<div className="av-modal-transcript-col">
								{isLoadingAV && (
									<div className="av-modal-transcript-skeleton">
										{Array.from({ length: 6 }).map((_, idx) => (
											<Skeleton key={idx} active paragraph={{ rows: 2 }} />
										))}
									</div>
								)}
								{/* Gated on playerLocation so the modal transcript stays mounted through the close
								    animation (it unmounts only after afterOpenChange flips playerLocation back to
								    PlayerLocation.Drawer), keeping it in sync with the player's move-back timing. */}
								{!isLoadingAV && playerLocation === PlayerLocation.Modal && (
									<TranscriptSection
										t={t}
										handleDownloadTranscript={handleDownloadTranscript}
										highlightedTranscriptItems={highlightedTranscriptItems}
										currentMentionIndex={currentMentionIndex}
										handlePreviousMention={handlePreviousMention}
										handleNextMention={handleNextMention}
										visibleList={visibleList}
										activeDialogIds={activeDialogIds}
										handleSeekToDialog={handleSeekToDialog}
										transcriptBodyRef={modalTranscriptBodyRef}
										searchInputValue={searchInput}
										setSearchInputValue={setSearchInput}
										searchQuery={searchQuery}
										highlightedDialogs={highlightedDialogs}
										autoScrollEnabled={autoScrollEnabled}
										onResumeAutoScroll={handleResumeAutoScroll}
										isVideo={isVideo}
										isPlaying={isPlaying}
										onSearchFocus={handleSearchFocus}
										onSearchClear={handleSearchClear}
										isPausedForSearch={isPausedForSearch}
										searchMatchCount={searchMatches.length}
										searchActiveMatchIndex={searchActiveMatchIndex}
										onPrevSearchMatch={handlePrevSearchMatch}
										onNextSearchMatch={handleNextSearchMatch}
									/>
								)}
							</div>
						</div>
					</div>
				</Modal>
			)}
		</div>
	);
};

export default React.memo(AudioVideoTranscriptView);
